import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import React, { forwardRef, ForwardedRef, ReactElement, useCallback, useImperativeHandle, useRef } from 'react';
import { ColumnState, FirstDataRenderedEvent, GridReadyEvent } from 'ag-grid-community';
import { createSaveColumnState, getRowStyle, onGridReady, restoreGridScrollState, saveGridScrollState, withScrollRestoreLoading } from 'src/utils/grid/gridStyleUtils';
import { HandledProps } from 'src/components/common/common';
import { useGridCellSelection } from 'src/utils/grid/GridHandlerContent';
import { ShiftRangeSelectionConfig, useShiftRangeRowSelection } from 'src/utils/grid/useShiftRangeRowSelection';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';

// Grid_Select에 필요한 props 정의. AgGridReactProps에서 공통 처리할 props는 제외합니다.
interface GridSelectProps<TData> extends Omit<AgGridReactProps<TData>, HandledProps> {
    setHoveredRowIndex: (index: number | null) => void;
    columnStateKey: string;
    setColumnState: (state: ColumnState[]) => void;
    // 부모에서 추가적인 onGridReady 로직을 실행할 경우를 대비
    onGridReady?: (params: GridReadyEvent<TData>) => void;
    /**
     * rowData 최초 렌더 완료 시 부모(Table 등)에서 추가 로직을 실행할 때 사용.
     * 내부 스크롤 복원 이후에 호출됩니다.
     */
    onFirstDataRendered?: (params: FirstDataRenderedEvent<TData>) => void;
    onColumnMoved?: () => void;
    onColumnPinned?: () => void;
    onColumnVisible?: () => void;
    onColumnResized?: () => void;
    shiftRangeSelection?: ShiftRangeSelectionConfig<TData>;
    scrollTop?: number | null;
    setScrollTop?: (top: number | null) => void;
    restoreScroll?: boolean;
}

// Grid_Select 컴포넌트 정의
function GridSelect<TData extends {} = any>(
    props: GridSelectProps<TData>,
    ref: ForwardedRef<AgGridReact<TData>>
): ReactElement {
    const {
        setHoveredRowIndex,
        columnStateKey,
        setColumnState,
        onGridReady: onGridReadyProp,
        onFirstDataRendered,
        onColumnMoved: onColumnMovedProp,
        onColumnPinned: onColumnPinnedProp,
        onColumnVisible: onColumnVisibleProp,
        onColumnResized: onColumnResizedProp,
        onRowClicked: onRowClickedProp,
        shiftRangeSelection,
        scrollTop,
        setScrollTop,
        restoreScroll = true,
        ...restProps
    } = props;

    // 내부에서 사용할 ref. 부모에게 받은 ref와 동기화됩니다.
    const gridRef = useRef<AgGridReact<TData>>(null);

    const {
        anchorCellRef,
        selectedCellRef,
        handleCellClick_targeting,
        handleCellCopy,
        handleCellMouseDown,
        handleCellMouseOver,
        clearRange,
		getSelectedCellRange,
        processedColDefs,
    } = useGridCellSelection({
        columnDefs: props.columnDefs,
    });

    useImperativeHandle(
        ref,
        () => {
            const inst = gridRef.current;
            if (!inst) return null as unknown as AgGridReact<TData>;
            return Object.assign(inst, {
                clearRange: () => {
                    const api = gridRef.current?.api;
                    if (api) clearRange(api);
                },
				getSelectedCellRange,
            }) as AgGridReact<TData>;
        },
		[clearRange, getSelectedCellRange]
    );
    
    const saveColumnState = createSaveColumnState(gridRef, columnStateKey, setColumnState);

    const handleColumnMoved = () => { saveColumnState(); onColumnMovedProp?.(); };
    const handleColumnPinned = () => { saveColumnState(); onColumnPinnedProp?.(); };
    const handleColumnVisible = () => { saveColumnState(); onColumnVisibleProp?.(); };
    const handleColumnResized = () => { saveColumnState(); onColumnResizedProp?.(); };

    const onRowClicked = useShiftRangeRowSelection<TData>({
        config: shiftRangeSelection,
        onRowClicked: onRowClickedProp,
    });

    // 사용자 스크롤 종료 시 현재 위치를 Store에 저장 (탭 전환 후 복원용)
    const handleBodyScrollEnd = useCallback(() => {
        const api = gridRef.current?.api;
        if (api && setScrollTop) {
            saveGridScrollState(api, setScrollTop);
        }
    }, [setScrollTop]);

    const { setLoadingSpinner } = useLoadingSpinnerStore();

    // rowData 최초 렌더 완료 후 Store에 저장된 스크롤 위치 복원 (탭 복귀·재마운트 시)
    const handleFirstDataRendered = useCallback((params: FirstDataRenderedEvent<TData>) => {
        if (restoreScroll && setScrollTop) {
            const shouldRestoreScroll = scrollTop != null && scrollTop >= 0;
            if (shouldRestoreScroll) {
                withScrollRestoreLoading(setLoadingSpinner, (onComplete) => {
                    restoreGridScrollState(params.api, scrollTop, onComplete);
                });
            } else {
                restoreGridScrollState(params.api, scrollTop);
            }
        }
        if (onFirstDataRendered) {
            onFirstDataRendered(params);
        }
    }, [restoreScroll, setScrollTop, scrollTop, onFirstDataRendered, setLoadingSpinner]);

    // 그리드가 준비되었을 때 실행될 공통 로직
    const handleGridReady = (params: GridReadyEvent<TData>) => {
        params.api.setGridOption('context', {
            ...restProps.context,
            cellSelectionRef: selectedCellRef,
        });
        onGridReady(gridRef, columnStateKey, setColumnState);
        if (onGridReadyProp) {
            onGridReadyProp(params);
        }
    };

    return (
        <div className="flex-1 ag-theme-alpine w-full overflow-y-auto relative">
            <AgGridReact<TData>
                ref={gridRef}
                overlayNoRowsTemplate={'<span style="padding: 32px;">조회된 데이터가 없습니다.</span>'}
                {...restProps}
                popupParent={restProps.popupParent ?? (typeof document !== 'undefined' ? document.body : undefined)}
                enableRangeSelection={true}
                columnDefs={processedColDefs}
                onCellClicked={handleCellClick_targeting}
                onCellMouseDown={handleCellMouseDown}
                onCellMouseOver={(params) => {
                    if (params.node.rowPinned !== 'bottom') setHoveredRowIndex(params.node.rowIndex);
                    handleCellMouseOver(params);
                }}
                onCellKeyDown={handleCellCopy}
                onRowClicked={onRowClicked}

                suppressScrollOnNewData={true}
                suppressColumnVirtualisation={true}
                suppressColumnMoveAnimation={true}
                getRowStyle={getRowStyle}

                onCellMouseOut={() => { setHoveredRowIndex(null); }}

                onGridReady={handleGridReady}
                onFirstDataRendered={handleFirstDataRendered}
                onBodyScrollEnd={handleBodyScrollEnd}
                onColumnMoved={handleColumnMoved}
                onColumnPinned={handleColumnPinned}
                onColumnVisible={handleColumnVisible}
                onColumnResized={handleColumnResized}
                onSortChanged={saveColumnState}
            />
        </div>
    );
}

// Grid_Select 컴포넌트를 내보내기
const Grid_Select = forwardRef(GridSelect) as <TData extends {} = any>(
    props: GridSelectProps<TData> & { ref?: ForwardedRef<AgGridReact<TData>> }
) => ReactElement;


(Grid_Select as React.FunctionComponent).displayName = "Grid_Select";

export default Grid_Select;
